import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import Pagination from "../view/common/pagination/Pagination";
import { ApiResponse, Country, CountryResponse } from "../models/countrytypes";
import { deleteData, getData, patchData, postData } from "../api/ApiServices";
import TableComponent from "./TableComponent";
import CustomAlert from "../view/common/alert/CustomAlert";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";


import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";

import customStyles from '../styles/currencyMaster.module.css'

const CountryMaster: React.FC = () => {
  const [countryData, setCountryData] = useState<Country[]>([]);
  const [allCountryData, setAllCountryData] = useState<Country[]>([]);
  const [currentCountry, setCurrentCountry] = useState<Country>({
    countryCode: "",
    countryName: "",
    status: "ACTIVE",
  });
  const [originalCountry, setOriginalCountry] = useState<Country>({
    countryCode: "",
    countryName: "",
    status: "ACTIVE",
  });
  const [editMode, setEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];
 
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);

 
  const [validationErrors, setValidationErrors] = useState<{
    countryCode?: string;
    countryName?: string;
    status?: string;
  }>({});

 
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  
  const [sortBy, setSortBy] = useState<string>("countryName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");


  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state:RootState) => state.permissions)
    
  const [initializeCountryMaster, setIsCountryMasterInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    setIsCountryMasterInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if(initializeCountryMaster){
      fetchCountries();
    }
  }, [initializeCountryMaster, currentPage, itemsPerPage, searchTerm, filters, sortBy, sortOrder]);

  useEffect(() => {
    if(initializeCountryMaster) {
      fetchAllCountries();
    }
  }, [initializeCountryMaster]);

  const fetchAllCountries = async () => {
    try {
      const response: any = await getData<ApiResponse<CountryResponse>>(
        "/country-masters"
        
      );
      setAllCountryData(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching all countries:", error);
    }
  };

  const fetchCountries = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData<ApiResponse<CountryResponse>>(
        "/country-masters",
        {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          filter: filters,
          sortBy: sortBy,
          sortOrder: sortOrder,
        }
      );

      setCountryData(response?.data?.data || []);
      setTotalItems(response?.data?.pagination.total || 0);
      setTotalPages(response?.data?.pagination.totalPages || 1);
      setError(null);
    } catch (error) {
      console.error("Error fetching countries:", error);
      setError("Failed to load country data.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

 
  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const validateForm = (): boolean => {
    const errors: {
      countryCode?: string;
      countryName?: string;
      status?: string;
    } = {};

    if (!currentCountry.countryCode.trim()) {
      errors.countryCode = "Country Code is required";
    }

    if (!currentCountry.countryName.trim()) {
      errors.countryName = "Country Name is required";
    }

    if (editMode && !currentCountry.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const hasChanges = (): boolean => {
    return (
      currentCountry.countryCode !== originalCountry.countryCode ||
      currentCountry.countryName !== originalCountry.countryName ||
      currentCountry.status !== originalCountry.status
    );
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const handleAddCountry = () => {
    const newCountry = { countryCode: "", countryName: "", status: "ACTIVE" };
    setCurrentCountry(newCountry);
    setOriginalCountry(newCountry);
    setValidationErrors({});
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEditCountry = (country: Country) => {
    setCurrentCountry({ ...country });
    setOriginalCountry({ ...country }); 
    setValidationErrors({});
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleDeleteCountry = async (id: string) => {
    const countryToDelete = countryData.find((country) => country._id === id);

    if (countryToDelete) {
      configureAlert(
        `Are you sure you want to Inactive ${countryToDelete.countryName}?`,
        async () => {
          closeAlert(); 

          try {
            await deleteData("/country-masters", id);
            await fetchCountries();
            await fetchAllCountries(); 
     
            configureAlert(
              `Country has been Inactive successfully.`,
              () => {
                closeAlert(); 
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 
            );
          } catch (error) {
            console.error("Error deleting country:", error);
            configureAlert(
              "Failed to delete country.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null 
      );
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return; 
    }

    if (editMode && !hasChanges()) {
      configureAlert(
        "No changes were made.",
        () => {
          setIsModalOpen(false);
          closeAlert();
        },
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000 
      );
      return;
    }

    setIsLoading(true);
    try {
      let updatedCountry = { ...currentCountry };

      if (editMode) {

        const response = await patchData<ApiResponse<CountryResponse>>(
          "/country-masters",
          currentCountry._id as string,
          updatedCountry
        );
        setIsModalOpen(false);

        configureAlert(
          `${currentCountry.countryName} has been updated successfully.`,
          () => {
            fetchCountries();
            fetchAllCountries();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        try {
          const response = await postData<ApiResponse<CountryResponse>>(
            "/country-masters",
            updatedCountry
          );
          setIsModalOpen(false);

          configureAlert(
            `${currentCountry.countryName} has been added successfully.`,
            () => {
              fetchCountries();
              fetchAllCountries(); 
              closeAlert(); 
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        } catch (error: any) {
          console.error("Error adding country:", error);
            configureAlert(
              error.message || `Failed to ${currentCountry ? "update" : "add"} country.`,
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
         
        }
      }
    } catch (error: any) {
      console.error("Error saving country:", error);
      if (error?.response?.status === 5004 || 
          (error?.response?.data?.statusCode === 5004) || 
          (error?.response?.data?.msg && error?.response?.data?.msg.includes("Already Exists"))) {
        configureAlert(
          error?.response?.data?.msg || "Country Data Already Exists",
          closeAlert,
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        configureAlert(
          `Failed to ${editMode ? "update" : "add"} country.`,
          closeAlert,
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      }
    }
    setIsLoading(false);
  };

  const handleModalClose = () => {
    if (isModalOpen && hasChanges()) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsModalOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentCountry((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selected: any, { name }: any) => {
    setCurrentCountry((prev) => ({
      ...prev,
      [name]: selected?.value || "",
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const columns = [
    { key: "countryCode", label: "Country Code", sortable: true },
    { key: "countryName", label: "Country Name", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>General</span>
              <span>Country Master</span>
            </div>
            {permission.menus["country-masters"]?.create ?
            <div className="items-button-container tooltip-wrapper">
              <button
                className="item-models-add-btn"
                onClick={handleAddCountry}
              >
                <Plus size={16} />
              </button>
            </div>
            :<></>}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {isLoading ? (
        <div className="spinner"></div>
      ) : (
        <div className="box-container">
          <TableComponent
            columns={columns}
            data={countryData}
            onEdit={handleEditCountry}
            onDelete={handleDeleteCountry}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          />
        </div>
      )}

      {isModalOpen && (
        <div className="item-models-modal-overlay">
          <div className="item-models-modal">
            <div className="item-models-modal-header">
              <h5>{editMode ? "Edit Country" : "Add New Country"}</h5>
              <button
                className="item-models-modal-close"
                onClick={handleModalClose}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSave} noValidate>
             <div className={customStyles["form-group"]}>
                <input
                  type="text"
                  name="countryCode"
                  placeholder="Country Code"
                  value={currentCountry.countryCode}
                  onChange={handleInputChange}
                  className={validationErrors.countryCode ? customStyles["input-error"] : ""}
                  required
                />
                {validationErrors.countryCode && (
                  <div className={customStyles["form-error-message"]}>
                    {validationErrors.countryCode}
                  </div>
                )}
              </div>

             <div className={customStyles["form-group"]}>
                <input
                  type="text"
                  name="countryName"
                  placeholder="Country Name"
                  value={currentCountry.countryName}
                  onChange={handleInputChange}
                  className={validationErrors.countryName ? customStyles["input-error"] : ""}
                  required
                />
                {validationErrors.countryName && (
                  <div className={customStyles["form-error-message"]}>
                    {validationErrors.countryName}
                  </div>
                )}
              </div>

              {editMode && (
                <div className="select-container">
                  <Select
                    name="status"
                    options={statusOptions}
                    value={statusOptions.find(
                      (option) => option.value === currentCountry.status
                    )}
                    onChange={(selected, action) =>
                      handleSelectChange(selected, action)
                    }
                    placeholder="Select Status"
                    className={validationErrors.status ? "select-error" : ""}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {validationErrors.status && (
                    <div className={customStyles["form-error-message"]}>
                      {validationErrors.status}
                    </div>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="submit-button"
              >
                {isLoading ? "Saving..." : editMode ? "Update" : "Add"}
              </button>
            </form>
          </div>
        </div>
      )}

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default CountryMaster;